// Sürücü Puantajı — sürücü detay modalı.
// Seçili sürücünün belirli bir aralık için ayrıntılı performans dökümü:
// özet, günlük trend, gün-gün servis listesi.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import Modal from "shared/components/UIElements/Modal";
import Button from "shared/components/FormElements/Button";
import LoadingSpinner from "shared/components/UIElements/LoadingSpinner";

import useDriverPerformanceApi from "./hooks/useDriverPerformanceApi";
import DailyBreakdown from "./components/DailyBreakdown";
import PerformanceTrend from "./components/PerformanceTrend";
import { addDays, daysInRange, formatKm, formatMinutes, formatNumber, longDate } from "./utils";

import "./DriverPerformance.css";

const DriverPerformanceDetailModal = ({ show, driver, range, onClose }) => {
  const { t } = useTranslation();
  const { isLoading, getDriverDetail } = useDriverPerformanceApi();

  const [data, setData] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  const start = range?.start;
  const end = range?.end;
  const mode = range?.mode || "performance";
  const daysSortOrder = range?.daysSortOrder || (mode === "upcoming" ? "asc" : "desc");

  const fetchDetail = useCallback(async () => {
    if (!driver?.id) return;
    setErrorMessage(null);
    try {
      const result = await getDriverDetail(driver.id, { start, end });
      setData(result);
    } catch (err) {
      setErrorMessage(err?.message || t("driverPerformance.fetchError"));
    }
  }, [driver, start, end, getDriverDetail, t]);

  useEffect(() => {
    if (show && driver?.id) {
      fetchDetail();
    }
    if (!show) {
      setData(null);
      setErrorMessage(null);
    }
  }, [show, driver, fetchDetail]);

  const periodDays = useMemo(() => daysInRange(start, end), [start, end]);

  const detailTitle =
    mode === "upcoming"
      ? t("driverPerformance.detail.title7")
      : t("driverPerformance.detail.titlePeriod", { days: periodDays });

  const trendSeries = useMemo(() => {
    if (!data?.start || !data?.end) return [];
    const byDate = new Map(data.days.map((d) => [d.date, d]));
    const series = [];
    for (let date = data.start; date <= data.end; date = addDays(date, 1)) {
      const day = byDate.get(date);
      series.push({
        date,
        count: day?.count ?? 0,
        km: day?.km ?? 0,
        minutes: day?.minutes ?? 0,
      });
    }
    return series;
  }, [data]);

  return (
    <Modal
      show={show}
      onCancel={onClose}
      onClose={onClose}
      className="dp-detail-modal"
      header={driver ? `${driver.name} — ${detailTitle}` : detailTitle}
      contentClass="dp-detail-modal__content"
      footerClass="dp-detail-modal__footer"
      footer={
        <Button type="button" inverse onClick={onClose}>
          {t("close")}
        </Button>
      }
    >
      {(start || end) && (
        <div className="dp-detail__range">
          {longDate(data?.start || start)} – {longDate(data?.end || end)}
        </div>
      )}

      {isLoading && !data && (
        <div className="dp-detail__loading">
          <LoadingSpinner />
          <span>{t("driverPerformance.loading")}</span>
        </div>
      )}

      {errorMessage && <div className="dp-detail__error">{errorMessage}</div>}

      {data && !errorMessage && (
        <>
          <div className="dp-detail__summary">
            <SummaryCard label={t("driverPerformance.summary.daysWorked")} value={formatNumber(data.totals.daysWorked)} />
            <SummaryCard label={t("driverPerformance.summary.services")} value={formatNumber(data.totals.count)} />
            <SummaryCard label={t("driverPerformance.summary.km")} value={formatKm(data.totals.km)} />
            <SummaryCard label={t("driverPerformance.summary.time")} value={formatMinutes(data.totals.minutes)} />
          </div>

          {trendSeries.length > 0 && (
            <div className="dp-detail__trend">
              <h4>{t("driverPerformance.detail.dailyTrend")}</h4>
              <PerformanceTrend
                series={trendSeries}
                metric="count"
                width={920}
                height={92}
                showValues
                showDateLabels
              />
            </div>
          )}

          <DailyBreakdown
            days={data.days}
            rangeStart={data.start}
            rangeEnd={data.end}
            sortOrder={daysSortOrder}
            emptyDayMessageKey={
              mode === "upcoming" ? "driverPerformance.detail.noScheduledDay" : "driverPerformance.detail.noWorkDay"
            }
            t={t}
          />
        </>
      )}

      {isLoading && data && <LoadingSpinner asOverlay />}
    </Modal>
  );
};

const SummaryCard = ({ label, value }) => (
  <div className="dp-summary-card">
    <span className="dp-summary-card__value">{value}</span>
    <span className="dp-summary-card__label">{label}</span>
  </div>
);

export default DriverPerformanceDetailModal;
